import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from 'react';

export interface ShellHeaderConfig {
  header?: ReactNode;
  subheader?: ReactNode;
  isSticky?: boolean;
  hideBottomBorder?: boolean;
  headerActions?: ReactNode | null;
  secondaryButtons?: ReactNode;
}

export interface ShellHeaderState {
  header?: ReactNode;
  subheader?: ReactNode;
  isSticky: boolean;
  hideBottomBorder: boolean;
  headerActions: ReactNode | null;
  secondaryButtons?: ReactNode;
  // Full-width content rendered below the title row but above the header's
  // bottom border. Managed independently of setHeader so a page and one of its
  // descendants can each own part of the header without clobbering each other.
  headerBelow: ReactNode | null;
}

type ShellHeaderAction =
  | { type: 'SET_HEADER'; payload: ShellHeaderConfig }
  | { type: 'RESET_HEADER' }
  | { type: 'SET_HEADER_BELOW'; payload: ReactNode | null };

type ShellHeaderDispatch = {
  setHeader: (config: ShellHeaderConfig) => void;
  resetHeader: () => void;
  setHeaderBelow: (node: ReactNode | null) => void;
};

const DEFAULT_HEADER_STATE: ShellHeaderState = {
  header: undefined,
  subheader: undefined,
  isSticky: false,
  hideBottomBorder: false,
  headerActions: null,
  secondaryButtons: undefined,
  headerBelow: null,
};

function shellHeaderReducer(state: ShellHeaderState, action: ShellHeaderAction): ShellHeaderState {
  if (action.type === 'RESET_HEADER') {
    return state === DEFAULT_HEADER_STATE ? state : DEFAULT_HEADER_STATE;
  }

  if (action.type === 'SET_HEADER_BELOW') {
    if (state.headerBelow === action.payload) {
      return state;
    }
    return { ...state, headerBelow: action.payload };
  }

  const config = action.payload;
  // headerBelow is intentionally preserved here so SET_HEADER (owned by the
  // page) does not wipe content set via setHeaderBelow (owned by a descendant).
  const nextState: ShellHeaderState = {
    header: config.header,
    subheader: config.subheader,
    isSticky: config.isSticky ?? false,
    hideBottomBorder: config.hideBottomBorder ?? false,
    headerActions: config.headerActions ?? null,
    secondaryButtons: config.secondaryButtons,
    headerBelow: state.headerBelow,
  };

  if (
    state.header === nextState.header &&
    state.subheader === nextState.subheader &&
    state.isSticky === nextState.isSticky &&
    state.hideBottomBorder === nextState.hideBottomBorder &&
    state.headerActions === nextState.headerActions &&
    state.secondaryButtons === nextState.secondaryButtons
  ) {
    return state;
  }

  return nextState;
}

const HeaderStateContext = createContext<ShellHeaderState | null>(null);
const HeaderDispatchContext = createContext<ShellHeaderDispatch | null>(null);

export function ShellHeaderProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(shellHeaderReducer, DEFAULT_HEADER_STATE);

  const setHeader = useCallback((config: ShellHeaderConfig) => {
    dispatch({ type: 'SET_HEADER', payload: config });
  }, []);

  const resetHeader = useCallback(() => {
    dispatch({ type: 'RESET_HEADER' });
  }, []);

  const setHeaderBelow = useCallback((node: ReactNode | null) => {
    dispatch({ type: 'SET_HEADER_BELOW', payload: node });
  }, []);

  const actions = useMemo<ShellHeaderDispatch>(
    () => ({
      setHeader,
      resetHeader,
      setHeaderBelow,
    }),
    [setHeader, resetHeader, setHeaderBelow],
  );

  return (
    <HeaderDispatchContext.Provider value={actions}>
      <HeaderStateContext.Provider value={state}>{children}</HeaderStateContext.Provider>
    </HeaderDispatchContext.Provider>
  );
}

export function useShellHeader(config: ShellHeaderConfig) {
  const dispatch = useContext(HeaderDispatchContext);

  if (!dispatch) {
    throw new Error('useShellHeader must be used within ShellHeaderProvider');
  }

  const {
    header,
    subheader,
    isSticky,
    hideBottomBorder,
    headerActions = null,
    secondaryButtons,
  } = config;

  useLayoutEffect(() => {
    dispatch.setHeader({
      header,
      subheader,
      isSticky,
      hideBottomBorder,
      headerActions,
      secondaryButtons,
    });
  }, [dispatch, header, subheader, isSticky, hideBottomBorder, headerActions, secondaryButtons]);

  useLayoutEffect(() => {
    return () => {
      dispatch.resetHeader();
    };
  }, [dispatch]);
}

/**
 * Render full-width content into the shell header (below the title row, above
 * the bottom border) from a component nested inside the page. This is managed
 * independently from useShellHeader, so the page can keep owning the title /
 * actions while a descendant contributes this row.
 */
export function useShellHeaderBelow(node: ReactNode | null) {
  const dispatch = useContext(HeaderDispatchContext);

  if (!dispatch) {
    throw new Error('useShellHeaderBelow must be used within ShellHeaderProvider');
  }

  useLayoutEffect(() => {
    dispatch.setHeaderBelow(node);
  }, [dispatch, node]);

  useLayoutEffect(() => {
    return () => {
      dispatch.setHeaderBelow(null);
    };
  }, [dispatch]);
}

export function useHeaderState(): ShellHeaderState {
  const state = useContext(HeaderStateContext);
  if (!state) {
    throw new Error('useHeaderState must be used within ShellHeaderProvider');
  }
  return state;
}
